import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { CheckCircle2, FileSearch, FlaskConical, Send, ShieldAlert } from 'lucide-react'
import { cmsService } from '@services/api'
import { useAuthStore } from '@store/authStore'

function prettyJson(value: unknown) {
  return JSON.stringify(value, null, 2)
}

function getLatraResultStatus(data: any): 'success' | 'warning' | 'error' {
  if (data?.jwksHealth && !data?.endpoint) {
    return data.jwksHealth.healthy ? 'success' : 'error'
  }
  if (data?.latraSuccess === true || data?.response?.body?.statusCode === 5000) return 'success'
  if (data?.authAccepted === true && data?.latraSuccess === false) return 'warning'
  if (data?.authAccepted === false || data?.response?.httpStatus === 401) return 'error'
  if (data?.success === true) return 'success'
  if (data?.success === false) return 'error'
  return 'warning'
}

function getLatraResultSummary(data: any): string {
  if (!data) return 'No result yet'
  if (data?.error?.message) return data.error.message
  if (data?.jwksHealth && !data?.endpoint) {
    return data.jwksHealth.healthy
      ? 'JWKS discovery and key endpoints are reachable'
      : 'JWKS endpoints are misconfigured — fix discovery/JWKS routing first'
  }
  if (data?.jwksHealth && data.jwksHealth.healthy === false) {
    return 'JWKS endpoints are misconfigured — fix discovery/JWKS routing first'
  }
  if (data?.authAccepted === false || data?.response?.httpStatus === 401) {
    return 'JWT rejected by LATRA (401) — check issuer, keys, and JWKS endpoints'
  }
  if (data?.latraSuccess === true) {
    return `LATRA accepted (${data?.latraStatusCode ?? 5000}: ${data?.latraMessage ?? 'Successful'})`
  }
  if (data?.latraStatusCode) {
    return `LATRA declined (${data.latraStatusCode}: ${data?.latraMessage ?? 'Failed'})`
  }
  return data?.latraMessage || 'Request completed — inspect response body'
}

function todayEAT() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Nairobi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/** Keep reportDate as YYYY-MM-DD even if an API/locale returns M/D/YYYY. */
function toIsoDate(value: string): string {
  const raw = String(value || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw)
  if (!slash) return raw
  const a = Number(slash[1])
  const b = Number(slash[2])
  const y = Number(slash[3])
  const month = a > 12 ? b : a
  const day = a > 12 ? a : b
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export default function LatraTestPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [selectedVehicleId, setSelectedVehicleId] = useState('')
  const [selectedDriverId, setSelectedDriverId] = useState('')
  const [selectedBookingId, setSelectedBookingId] = useState('')
  const [reportDate, setReportDate] = useState(todayEAT())
  const [lastResult, setLastResult] = useState<any>(null)

  const overviewQuery = useQuery('latra-overview', () => cmsService.getLatraOverview(), {
    retry: false,
  })
  const controlQuery = useQuery('latra-control', () => cmsService.getLatraControl(), {
    retry: false,
  })
  const submissionsQuery = useQuery(
    'latra-submissions',
    () => cmsService.getLatraSubmissions({ take: 20 }),
    { retry: false }
  )

  const updateControl = useMutation(
    (payload: Record<string, unknown>) => cmsService.updateLatraControl(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('latra-control')
        queryClient.invalidateQueries('latra-overview')
        toast({ title: 'LATRA control updated', status: 'success', duration: 2500 })
      },
      onError: (error: any) => {
        toast({
          title: 'Failed to update LATRA control',
          description: error?.response?.data?.error?.message || error.message,
          status: 'error',
        })
      },
    }
  )

  const vehiclesQuery = useQuery('latra-vehicle-candidates', () => cmsService.getLatraVehicleCandidates(), {
    retry: false,
  })
  const driversQuery = useQuery('latra-driver-candidates', () => cmsService.getLatraDriverCandidates(), {
    retry: false,
  })
  const ticketsQuery = useQuery('latra-ticket-candidates', () => cmsService.getLatraTicketCandidates(), {
    retry: false,
  })
  const dailyPreviewQuery = useQuery(['latra-daily-preview', reportDate], () => cmsService.getLatraDailyReportPreview(reportDate), {
    retry: false,
  })
  const dailyCandidatesQuery = useQuery(
    'latra-daily-candidates',
    () => cmsService.getLatraDailyReportCandidates(3650),
    {
      retry: false,
      onSuccess: (res) => {
        const rows = res?.data || []
        if (rows.length > 0) {
          setReportDate((current) => {
            const currentIso = toIsoDate(current)
            const stillValid = rows.some((row: any) => toIsoDate(String(row.date)) === currentIso)
            return stillValid ? currentIso : toIsoDate(String(rows[0].date))
          })
        }
      },
    }
  )

  const handleLatraResult = (title: string, data: any) => {
    setLastResult(data)
    const status = getLatraResultStatus(data)
    toast({
      title,
      description: getLatraResultSummary(data),
      status,
      duration: 6000,
      isClosable: true,
    })
  }

  const handleBuildSuccess = (title: string, data: any) => {
    setLastResult(data)
    toast({
      title,
      status: 'success',
      duration: 3500,
      isClosable: true,
    })
  }

  const handleError = (title: string, error: any) => {
    const payload = error?.response?.data || { error: error?.message || 'Unknown error' }
    setLastResult(payload)
    toast({
      title,
      description: payload?.error?.message || payload?.message || error?.message || 'Request failed',
      status: 'error',
      duration: 7000,
      isClosable: true,
    })
  }

  const runPassthroughOnce = useMutation(() => cmsService.runLatraPassthroughOnce(), {
    onSuccess: (data) => {
      queryClient.invalidateQueries('latra-submissions')
      queryClient.invalidateQueries('latra-control')
      handleLatraResult('Passthrough tick finished', data)
    },
    onError: (error) => handleError('Passthrough tick failed', error),
  })

  const authProbe = useMutation(() => cmsService.probeLatraAuth(), {
    onSuccess: (data) => handleLatraResult('LATRA auth probe completed', data),
    onError: (error) => handleError('LATRA auth probe failed', error),
  })

  const refreshJwksHealth = useMutation(() => cmsService.getLatraJwksHealth(), {
    onSuccess: (data) => {
      overviewQuery.refetch()
      const health = data?.data
      setLastResult({ jwksHealth: health, success: health?.healthy === true })
      toast({
        title: 'JWKS health refreshed',
        description: health?.healthy ? 'Discovery and JWKS endpoints are healthy' : 'JWKS endpoints need attention',
        status: health?.healthy ? 'success' : 'error',
        duration: 5000,
        isClosable: true,
      })
    },
    onError: (error) => handleError('JWKS health check failed', error),
  })

  const buildVehicle = useMutation(() => cmsService.buildLatraVehiclePayload(selectedVehicleId), {
    onSuccess: (data) => handleBuildSuccess('Vehicle payload built', data),
    onError: (error) => handleError('Vehicle payload build failed', error),
  })

  const submitVehicle = useMutation(() => cmsService.submitLatraVehicle(selectedVehicleId), {
    onSuccess: (data) => handleLatraResult('Vehicle submission completed', data),
    onError: (error) => handleError('Vehicle submission failed', error),
  })

  const buildDriver = useMutation(() => cmsService.buildLatraDriverPayload(selectedDriverId), {
    onSuccess: (data) => handleBuildSuccess('Driver payload built', data),
    onError: (error) => handleError('Driver payload build failed', error),
  })

  const submitDriver = useMutation(() => cmsService.submitLatraDriver(selectedDriverId), {
    onSuccess: (data) => handleLatraResult('Driver submission completed', data),
    onError: (error) => handleError('Driver submission failed', error),
  })

  const buildTicket = useMutation(() => cmsService.buildLatraTicketPayload(selectedBookingId), {
    onSuccess: (data) => handleBuildSuccess('Ticket payload built', data),
    onError: (error) => handleError('Ticket payload build failed', error),
  })

  const submitTicket = useMutation(() => cmsService.submitLatraTicket(selectedBookingId), {
    onSuccess: (data) => handleLatraResult('Ticket submission completed', data),
    onError: (error) => handleError('Ticket submission failed', error),
  })

  const buildDaily = useMutation(() => cmsService.buildLatraDailyReport(reportDate), {
    onSuccess: (data) => handleBuildSuccess('Daily report payload built', data),
    onError: (error) => handleError('Daily report build failed', error),
  })

  const submitDaily = useMutation(() => cmsService.submitLatraDailyReport(reportDate), {
    onSuccess: (data) => handleLatraResult('Daily report submission completed', data),
    onError: (error) => handleError('Daily report submission failed', error),
  })

  const submitDailyBulk = useMutation(
    (dates: string[]) => cmsService.submitLatraDailyReportsBulk(dates),
    {
      onSuccess: (data) => handleLatraResult('Bulk daily report submission completed', data),
      onError: (error) => handleError('Bulk daily report submission failed', error),
    }
  )

  const busy =
    overviewQuery.isLoading ||
    vehiclesQuery.isLoading ||
    driversQuery.isLoading ||
    ticketsQuery.isLoading

  const overview = overviewQuery.data?.data
  const vehicles = vehiclesQuery.data?.data || []
  const drivers = driversQuery.data?.data || []
  const tickets = ticketsQuery.data?.data || []
  const dailyPreview = dailyPreviewQuery.data?.data
  const dailyCandidates = dailyCandidatesQuery.data?.data || []
  const loadError =
    overviewQuery.error ||
    vehiclesQuery.error ||
    ticketsQuery.error ||
    dailyPreviewQuery.error
  const loadErrorMessage =
    (loadError as any)?.response?.data?.error?.message ||
    (loadError as any)?.message ||
    'Failed to load LATRA test data.'

  const selectedVehicle = useMemo(
    () => vehicles.find((item: any) => item.id === selectedVehicleId),
    [vehicles, selectedVehicleId]
  )

  const selectedDriver = useMemo(
    () => drivers.find((item: any) => item.id === selectedDriverId),
    [drivers, selectedDriverId]
  )

  const selectedTicket = useMemo(
    () => tickets.find((item: any) => item.id === selectedBookingId),
    [tickets, selectedBookingId]
  )

  const submitVehiclesBulk = useMutation(
    (vehicleIds: string[]) => cmsService.submitLatraVehiclesBulk(vehicleIds),
    {
      onSuccess: (data) => handleLatraResult('Bulk vehicle submission completed', data),
      onError: (error) => handleError('Bulk vehicle submission failed', error),
    }
  )

  const submitDriversBulk = useMutation(
    (driverIds: string[]) => cmsService.submitLatraDriversBulk(driverIds),
    {
      onSuccess: (data) => handleLatraResult('Bulk driver submission completed', data),
      onError: (error) => handleError('Bulk driver submission failed', error),
    }
  )

  const lastResultStatus = getLatraResultStatus(lastResult)
  const lastResultSummary = getLatraResultSummary(lastResult)

  if (user?.role !== 'admin') {
    return (
      <Alert status="error" borderRadius="xl">
        <AlertIcon />
        <Box>
          <AlertTitle>Admin Access Required</AlertTitle>
          <AlertDescription>This LATRA test unit is restricted to admin users.</AlertDescription>
        </Box>
      </Alert>
    )
  }

  if (busy) {
    return (
      <VStack py={16}>
        <Spinner size="xl" color="blue.500" />
        <Text color="gray.600">Loading LATRA test unit...</Text>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" align="start">
        <Box>
          <Heading size="lg">LATRA Test Unit</Heading>
          <Text color="gray.600" mt={2}>
            Admin-only test bench for JWT auth, vehicle submission, ticket submission, and daily report submission.
          </Text>
        </Box>
        <Badge colorScheme={overview?.enabled ? 'green' : 'red'} px={3} py={2} borderRadius="full">
          {overview?.enabled ? 'LATRA Enabled' : 'LATRA Disabled'}
        </Badge>
      </HStack>

      <Alert status="info" borderRadius="xl">
        <AlertIcon />
        <Box>
          <AlertTitle>Detailed Diagnostics Enabled</AlertTitle>
          <AlertDescription>
            Every submit action returns the exact request payload, JWT claim summary, HTTP status, and LATRA response body so failures can be traced quickly.
          </AlertDescription>
        </Box>
      </Alert>

      <Card borderRadius="2xl" borderWidth="1px" borderColor="orange.200">
        <CardHeader>
          <HStack justify="space-between">
            <Box>
              <Heading size="md">LATRA Traffic Control</Heading>
              <Text color="gray.600" fontSize="sm" mt={1}>
                Kill-switch and auto-sync. Mobile apps never wait on LATRA — sync runs in CMS only.
                Defaults are OFF so production traffic is unchanged until you enable them.
              </Text>
            </Box>
            <Badge colorScheme={controlQuery.data?.data?.canCallLatra ? 'green' : 'gray'}>
              {controlQuery.data?.data?.canCallLatra ? 'Outbound ON' : 'Outbound OFF'}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box pr={4}>
                <FormLabel mb={0}>Allow outbound to CeTS</FormLabel>
                <Text fontSize="xs" color="gray.500">Master runtime switch (also needs LATRA_ENABLED=true)</Text>
              </Box>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.outboundEnabled)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ outboundEnabled: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box pr={4}>
                <FormLabel mb={0}>Auto passthrough</FormLabel>
                <Text fontSize="xs" color="gray.500">Background worker pushes unpaid-synced records</Text>
              </Box>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.passthroughEnabled)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ passthroughEnabled: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>Auto-sync vehicles</FormLabel>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.syncVehicles)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ syncVehicles: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>Auto-sync drivers</FormLabel>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.syncDrivers)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ syncDrivers: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>Auto-sync trips</FormLabel>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.syncTrips)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ syncTrips: e.target.checked })}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>Auto-sync daily reports</FormLabel>
              <Switch
                isChecked={Boolean(controlQuery.data?.data?.settings?.syncDailyReports)}
                isDisabled={updateControl.isLoading}
                onChange={(e) => updateControl.mutate({ syncDailyReports: e.target.checked })}
              />
            </FormControl>
          </SimpleGrid>
          <HStack spacing={3} mb={4}>
            <Button
              size="sm"
              colorScheme="orange"
              leftIcon={<Send size={16} />}
              onClick={() => runPassthroughOnce.mutate()}
              isLoading={runPassthroughOnce.isLoading}
            >
              Run passthrough once
            </Button>
            <Text fontSize="sm" color="gray.500">
              Success {controlQuery.data?.data?.stats?.success ?? 0} · Failed {controlQuery.data?.data?.stats?.failed ?? 0} · Skipped {controlQuery.data?.data?.stats?.skipped ?? 0}
            </Text>
          </HStack>
          <Box>
            <Text fontWeight="semibold" mb={2}>Recent submissions (saved locally)</Text>
            <Textarea
              value={prettyJson(submissionsQuery.data?.data?.items || [])}
              minH="180px"
              fontFamily="mono"
              fontSize="xs"
              isReadOnly
            />
          </Box>
        </CardBody>
      </Card>

      {Boolean(loadError) && (
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          <Box>
            <AlertTitle>Test Data Load Failed</AlertTitle>
            <AlertDescription>{String(loadErrorMessage)}</AlertDescription>
          </Box>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>JWT Identity</StatLabel>
              <StatNumber fontSize="md">{overview?.issuer || 'n/a'}</StatNumber>
              <StatHelpText>iss / sub</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>JWKS Host</StatLabel>
              <StatNumber fontSize="md">{overview?.jwksHost || 'n/a'}</StatNumber>
              <StatHelpText>{overview?.jwksUrl || 'JWKS URL'}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Audience</StatLabel>
              <StatNumber fontSize="md">{overview?.audience || 'n/a'}</StatNumber>
              <StatHelpText>Expected aud claim</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>LATRA Base URL</StatLabel>
              <StatNumber fontSize="md">{overview?.baseUrl || 'n/a'}</StatNumber>
              <StatHelpText>Current submission target</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card borderRadius="2xl">
        <CardHeader>
          <HStack justify="space-between">
            <HStack>
              <ShieldAlert size={18} />
              <Heading size="md">JWKS & Discovery Health</Heading>
            </HStack>
            <Badge colorScheme={overview?.jwksHealth?.healthy ? 'green' : 'red'}>
              {overview?.jwksHealth?.healthy ? 'Healthy' : 'Needs Fix'}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <Text color="gray.600" fontSize="sm">
              Discovery: {overview?.discoveryUrl || 'n/a'} → JWKS: {overview?.jwksUrl || 'n/a'}
            </Text>
            {(overview?.jwksHealth?.checks || []).map((check: any) => (
              <Alert key={check.url} status={check.ok ? 'success' : 'error'} borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">{check.url}</AlertTitle>
                  <AlertDescription fontSize="sm">{check.detail}</AlertDescription>
                </Box>
              </Alert>
            ))}
            <Button
              size="sm"
              alignSelf="start"
              variant="outline"
              isLoading={refreshJwksHealth.isLoading}
              onClick={() => refreshJwksHealth.mutate()}
            >
              Refresh JWKS Health
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <Card borderRadius="2xl">
        <CardHeader>
          <HStack>
            <ShieldAlert size={18} />
            <Heading size="md">Authentication Probe</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Text color="gray.600">
              Verifies JWKS endpoints, issues a live JWT, and submits a single valid vehicle to confirm auth and payload acceptance.
              Auth passes if you do not get HTTP 401. LATRA success requires statusCode 5000.
            </Text>
            <Button
              leftIcon={<FlaskConical size={16} />}
              colorScheme="blue"
              alignSelf="start"
              isLoading={authProbe.isLoading}
              onClick={() => authProbe.mutate()}
            >
              Run Auth Probe
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
        <Card borderRadius="2xl">
          <CardHeader>
            <HStack>
              <FileSearch size={18} />
              <Heading size="md">Vehicle Submission Test</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Select vehicle</FormLabel>
                <Select placeholder="Choose vehicle" value={selectedVehicleId} onChange={(e) => setSelectedVehicleId(e.target.value)}>
                  {vehicles.map((vehicle: any) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registrationNumber} | {vehicle.currentRoute?.startPoint || 'No route'} - {vehicle.currentRoute?.endPoint || 'No route'}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {vehicles.length === 0 && (
                <Alert status="warning" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>No routable vehicles are available for LATRA testing yet.</AlertDescription>
                </Alert>
              )}
              {selectedVehicle && (
                <Alert status="info" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>
                    Owner: {selectedVehicle.owner?.firstName || '-'} {selectedVehicle.owner?.lastName || ''} | Route: {selectedVehicle.currentRoute?.startPoint || '-'} to {selectedVehicle.currentRoute?.endPoint || '-'}
                  </AlertDescription>
                </Alert>
              )}
              <HStack flexWrap="wrap">
                <Button leftIcon={<FileSearch size={16} />} onClick={() => buildVehicle.mutate()} isDisabled={!selectedVehicleId} isLoading={buildVehicle.isLoading}>
                  Build Payload
                </Button>
                <Button colorScheme="blue" leftIcon={<Send size={16} />} onClick={() => submitVehicle.mutate()} isDisabled={!selectedVehicleId} isLoading={submitVehicle.isLoading}>
                  Submit Vehicle
                </Button>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<Send size={16} />}
                  onClick={() => submitVehiclesBulk.mutate(vehicles.map((v: any) => v.id))}
                  isDisabled={vehicles.length === 0}
                  isLoading={submitVehiclesBulk.isLoading}
                >
                  Submit All ({vehicles.length})
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Card borderRadius="2xl">
          <CardHeader>
            <HStack>
              <FileSearch size={18} />
              <Heading size="md">Driver Submission Test</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Select driver</FormLabel>
                <Select placeholder="Choose driver" value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}>
                  {drivers.map((driver: any) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.firstName} {driver.lastName} | {driver.phoneNumber}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {drivers.length === 0 && (
                <Alert status="warning" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>No drivers are available for LATRA testing yet.</AlertDescription>
                </Alert>
              )}
              {selectedDriver && (
                <Alert status="info" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>
                    License: {selectedDriver.licenseNumber || '-'} | Email: {selectedDriver.email || '-'}
                  </AlertDescription>
                </Alert>
              )}
              <HStack flexWrap="wrap">
                <Button leftIcon={<FileSearch size={16} />} onClick={() => buildDriver.mutate()} isDisabled={!selectedDriverId} isLoading={buildDriver.isLoading}>
                  Build Payload
                </Button>
                <Button colorScheme="blue" leftIcon={<Send size={16} />} onClick={() => submitDriver.mutate()} isDisabled={!selectedDriverId} isLoading={submitDriver.isLoading}>
                  Submit Driver
                </Button>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<Send size={16} />}
                  onClick={() => submitDriversBulk.mutate(drivers.map((d: any) => d.id))}
                  isDisabled={drivers.length === 0}
                  isLoading={submitDriversBulk.isLoading}
                >
                  Submit All ({drivers.length})
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Card borderRadius="2xl">
          <CardHeader>
            <HStack>
              <FileSearch size={18} />
              <Heading size="md">Ticket Submission Test</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Select completed paid booking</FormLabel>
                <Select placeholder="Choose booking" value={selectedBookingId} onChange={(e) => setSelectedBookingId(e.target.value)}>
                  {tickets.map((booking: any) => (
                    <option key={booking.id} value={booking.id}>
                      {booking.bookingNumber} | {booking.trip?.daladala?.registrationNumber || 'No vehicle'} | {booking.boardingStop?.name} to {booking.alightingStop?.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {tickets.length === 0 && (
                <Alert status="warning" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>No completed paid bookings are currently eligible for LATRA ticket submission tests.</AlertDescription>
                </Alert>
              )}
              {selectedTicket && (
                <Alert status="info" borderRadius="xl">
                  <AlertIcon />
                  <AlertDescription>
                    Fare: TZS {selectedTicket.fareAmount} | Vehicle: {selectedTicket.trip?.daladala?.registrationNumber || '-'} | Trip status: {selectedTicket.trip?.status || '-'}
                  </AlertDescription>
                </Alert>
              )}
              <HStack>
                <Button leftIcon={<FileSearch size={16} />} onClick={() => buildTicket.mutate()} isDisabled={!selectedBookingId} isLoading={buildTicket.isLoading}>
                  Build Payload
                </Button>
                <Button colorScheme="blue" leftIcon={<Send size={16} />} onClick={() => submitTicket.mutate()} isDisabled={!selectedBookingId} isLoading={submitTicket.isLoading}>
                  Submit Ticket
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card borderRadius="2xl">
        <CardHeader>
          <HStack>
            <CheckCircle2 size={18} />
            <Heading size="md">Daily Summary Test</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                CeTS rejects empty daily reports (0 trips). Choose any historical day that has paid bookings from the list
                (up to ~10 years), or pick a date manually — Submit stays enabled whenever that day has trips &gt; 0.
              </AlertDescription>
            </Alert>
            {dailyCandidates.length > 0 && (
              <FormControl maxW="520px">
                <FormLabel>Days with paid bookings (all history, newest first)</FormLabel>
                <Select
                  value={reportDate}
                  onChange={(e) => setReportDate(toIsoDate(e.target.value))}
                  placeholder="Select a day with trips"
                >
                  {dailyCandidates.map((row: any) => {
                    const iso = toIsoDate(String(row.date))
                    return (
                      <option key={iso} value={iso}>
                        {iso} — {row.totalTrip} trips — TZS {row.totalPaidAmount}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            )}
            <FormControl maxW="280px">
              <FormLabel>Report date</FormLabel>
              <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
            </FormControl>
            {dailyPreview && (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Card bg="gray.50">
                  <CardBody><Text fontWeight="semibold">Trips: {dailyPreview.totalTrip}</Text></CardBody>
                </Card>
                <Card bg="gray.50">
                  <CardBody><Text fontWeight="semibold">Paid amount: TZS {dailyPreview.totalPaidAmount}</Text></CardBody>
                </Card>
                <Card bg="gray.50">
                  <CardBody><Text fontWeight="semibold">Report date: {dailyPreview.reportDate}</Text></CardBody>
                </Card>
              </SimpleGrid>
            )}
            {dailyPreview && dailyPreview.totalTrip === 0 && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This day has 0 paid bookings under the LATRA daily filters. Pick another date from the list (or another
                  calendar day that had payments).
                </AlertDescription>
              </Alert>
            )}
            <HStack flexWrap="wrap">
              <Button leftIcon={<FileSearch size={16} />} onClick={() => buildDaily.mutate()} isLoading={buildDaily.isLoading}>
                Build Daily Report
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<Send size={16} />}
                onClick={() => submitDaily.mutate()}
                isLoading={submitDaily.isLoading}
                isDisabled={!reportDate || (dailyPreview != null && dailyPreview.totalTrip === 0)}
              >
                Submit Daily Report
              </Button>
              <Button
                colorScheme="orange"
                variant="outline"
                leftIcon={<Send size={16} />}
                onClick={() =>
                  submitDailyBulk.mutate(dailyCandidates.map((row: any) => toIsoDate(String(row.date))).slice(0, 90))
                }
                isLoading={submitDailyBulk.isLoading}
                isDisabled={dailyCandidates.length === 0}
              >
                Submit days with data (max 90)
              </Button>
            </HStack>
            {dailyCandidates.length === 0 && !dailyCandidatesQuery.isLoading && (
              <Text fontSize="sm" color="gray.500">
                No paid bookings found in history (confirmed/boarded/completed). Check payment_status = paid in the DB.
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      <Card borderRadius="2xl">
        <CardHeader>
          <Heading size="md">Last Result</Heading>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            {lastResult && (
              <Alert status={lastResultStatus} borderRadius="xl">
                <AlertIcon />
                <Box>
                  <AlertTitle>Result Summary</AlertTitle>
                  <AlertDescription>{lastResultSummary}</AlertDescription>
                </Box>
              </Alert>
            )}
            <Text color="gray.600">
              This panel shows the latest backend result exactly as returned by the admin LATRA endpoint.
            </Text>
            <Textarea minH="480px" fontFamily="mono" fontSize="sm" value={lastResult ? prettyJson(lastResult) : 'No LATRA action executed yet.'} readOnly />
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
